import { Controller } from "@hotwired/stimulus"

// Sends watch_duration + current playhead position every 10s while playing.
// Server auto-marks the lesson complete at 90% of total duration; when that
// happens we reload so the sidebar checkmark + next-lesson CTA refresh.
export default class extends Controller {
  static values = {
    lessonId: Number,
    courseId: Number,
    coursePath: String,
    lessonPath: String,
    videoId: String,
    resumeAt: { type: Number, default: 0 },
    videoDuration: { type: Number, default: 0 }
  }

  connect() {
    this.watchTime = 0
    this.lastUpdate = Date.now()
    this.updateInterval = null
    this.player = null
    this.lastPosition = 0
    this.autoCompleted = false

    this.extractVideoId()

    // Two video paths: HTML5 <video> (Cloudflare HLS) and YouTube iframe.
    const html5 = this.element.querySelector("video[data-video-player-target='video']")
    if (html5) {
      this.bindHtml5(html5)
    } else {
      this.loadYouTubeAPI()
    }
  }

  // ───────── HTML5 path (Cloudflare HLS / direct mp4) ──────────────────────
  bindHtml5(video) {
    this.html5 = video

    const onLoaded = () => {
      if (this.resumeAtValue > 0 && Number.isFinite(video.duration) && video.duration > this.resumeAtValue) {
        try { video.currentTime = this.resumeAtValue } catch (_) {}
      }
    }
    if (video.readyState >= 1) onLoaded()
    else video.addEventListener("loadedmetadata", onLoaded, { once: true })

    video.addEventListener("play",  () => this.startTracking())
    video.addEventListener("pause", () => this.stopTracking())
    video.addEventListener("ended", () => this.stopTracking())
  }

  // ───────── YouTube path ─────────────────────────────────────────────────
  extractVideoId() {
    const iframe = this.element.querySelector("iframe")
    if (iframe && iframe.src) {
      const match = iframe.src.match(/embed\/([^?]+)/)
      if (match) this.videoIdValue = match[1]
    }
  }

  loadYouTubeAPI() {
    if (window.YT && window.YT.Player) {
      this.initYouTubePlayer()
      return
    }
    if (!window.onYouTubeIframeAPIReady) {
      const tag = document.createElement("script")
      tag.src = "https://www.youtube.com/iframe_api"
      const first = document.getElementsByTagName("script")[0]
      first.parentNode.insertBefore(tag, first)
      window.onYouTubeIframeAPIReady = () => {
        document.dispatchEvent(new CustomEvent("youtube-api-ready"))
      }
    }
    document.addEventListener(
      "youtube-api-ready",
      () => this.initYouTubePlayer(),
      { once: true }
    )
  }

  initYouTubePlayer() {
    if (!this.videoIdValue) return
    const iframe = this.element.querySelector("iframe")
    if (!iframe) return

    try {
      this.player = new YT.Player(iframe, {
        videoId: this.videoIdValue,
        events: {
          onReady: (e) => this.onYTReady(e),
          onStateChange: (e) => this.onYTStateChange(e)
        }
      })
    } catch (_) {}
  }

  onYTReady(_event) {
    if (this.resumeAtValue > 0 && this.player?.seekTo) {
      try { this.player.seekTo(this.resumeAtValue, true) } catch (_) {}
    }
  }

  onYTStateChange(event) {
    // 1 PLAYING, 2 PAUSED, 0 ENDED
    if (event.data === 1) this.startTracking()
    else if (event.data === 2 || event.data === 0) this.stopTracking()
  }

  // ───────── Tracking loop (shared) ───────────────────────────────────────
  startTracking() {
    if (this.updateInterval) return
    this.lastUpdate = Date.now()
    this.updateInterval = setInterval(() => this.updateWatchTime(), 10000)
  }

  stopTracking() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
      this.updateInterval = null
      this.updateWatchTime()
    }
  }

  currentPosition() {
    if (this.html5 && Number.isFinite(this.html5.currentTime)) return Math.floor(this.html5.currentTime)
    if (this.player?.getCurrentTime) {
      try { return Math.floor(this.player.getCurrentTime()) } catch (_) { return 0 }
    }
    return 0
  }

  currentDuration() {
    if (this.html5 && Number.isFinite(this.html5.duration)) return Math.floor(this.html5.duration)
    if (this.player?.getDuration) {
      try { return Math.floor(this.player.getDuration()) } catch (_) {}
    }
    return this.videoDurationValue || 0
  }

  updateWatchTime() {
    const now = Date.now()
    const elapsed = Math.floor((now - this.lastUpdate) / 1000)
    this.watchTime = elapsed
    this.lastUpdate = now
    this.lastPosition = this.currentPosition()
    this.sendWatchTime()
  }

  sendWatchTime() {
    if (this.watchTime === 0 && this.lastPosition === 0) return

    const url = `/courses/${this.coursePathValue}/lessons/${this.lessonPathValue}/track_watch`

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept":       "application/json",
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify({
        watch_duration: this.watchTime,
        position:       this.lastPosition,
        duration:       this.currentDuration()
      })
    })
      .then((r) => (r.ok ? r.json().catch(() => ({})) : null))
      .then((data) => {
        if (data?.auto_completed && !this.autoCompleted) {
          this.autoCompleted = true
          this.stopTracking()
          // Reload so the sidebar marks the lesson done and the Next button
          // points to the right place.
          setTimeout(() => window.location.reload(), 600)
        }
      })
      .catch(() => {})
  }

  disconnect() {
    this.stopTracking()
  }
}
